import { ClinicContextStore, isExternalUserToken } from '@surglogs-app/auth'
import {
  getClinicIdFromStorage,
  setClinicIdToStorage,
} from '@surglogs-app/auth/src/helpers/storageAccess'
import {
  convertEndpointProviderToFetchArgs,
  EnvApiResolver,
  createAuthHeaders,
  ENDPOINT_CLINICS_MANAGER_CLINICS,
  DEFAULT_CLINICS_MANAGER_API_VERSION,
  ClinicAPIEntity,
  getClinicsManagerClinic,
} from '@surglogs-app/network'
import {
  getDashboardRoute,
  getStandaloneLoginRoute,
  getUserDeactivatedRoute,
  redirectToExternalUrl,
} from '@surglogs-app/routing'

import { config } from '../app/config'
import { deriveClinicListSorted } from '../derivations/clinicList'

const changeDefaultClinic = async (
  token: string,
  userId: number,
  clinicId?: number,
): Promise<null | ClinicAPIEntity> => {
  const fetcher = convertEndpointProviderToFetchArgs({
    baseUrl: EnvApiResolver.getUrl(),
    version: config.appVersion,
    headers: createAuthHeaders(token),
  })

  const getClinicsResponse = await fetch(
    ...fetcher({
      method: 'get',
      url: {
        endpoint: ENDPOINT_CLINICS_MANAGER_CLINICS,
        params: [DEFAULT_CLINICS_MANAGER_API_VERSION],
      },
    }),
  )

  if (!getClinicsResponse.ok && !isExternalUserToken(token)) {
    redirectToExternalUrl(getStandaloneLoginRoute(), { replace: true })
  }

  const data = JSON.parse(await getClinicsResponse.text())
  const result = deriveClinicListSorted(data?.clinics ?? []).filter(
    (clinic: ClinicAPIEntity) => clinic.id !== clinicId,
  )

  if (result.length > 0) {
    setClinicIdToStorage(result[0]?.id, userId)

    // we don't want to redirect EXTERNAL users to dashboard
    if (!isExternalUserToken(token)) {
      window.location.pathname = getDashboardRoute()
    }

    return result[0]
  } else {
    setClinicIdToStorage(null, userId)

    if (window.location.pathname !== getUserDeactivatedRoute()) {
      window.location.pathname = getUserDeactivatedRoute()
    }

    return null
  }
}

const fetchClinicData =
  (userId: number) => async (clinicId: number, token: string) => {
    const fetcher = convertEndpointProviderToFetchArgs({
      baseUrl: EnvApiResolver.getUrl(),
      version: config.appVersion,
      headers: createAuthHeaders(token),
    })

    return getClinicsManagerClinic({ clinicId })(async (request) => {
      const response = await fetch(
        ...fetcher({ method: request.method, url: request.url }),
      )

      if (response.ok) {
        const data = JSON.parse(await response.text())

        return data
      } else {
        const defaultClinic = await changeDefaultClinic(token, userId, clinicId)

        return defaultClinic
      }
    })
  }

export const createClinicProvider = (auth: Auth) => {
  const { contextStore, tokenStore, contextDataStore } = auth
  const token = tokenStore.getToken()
  const userId = contextStore.getUserId()
  const clinicId = Number(getClinicIdFromStorage(userId))

  const clinicRefetcher = async (newClinicId: number, currentToken: string) => {
    const clinicData = await fetchClinicData(Number(userId))(
      newClinicId,
      currentToken,
    )

    await contextDataStore.fetchData()

    return clinicData
  }

  if (!clinicId && token && userId) {
    changeDefaultClinic(token, userId)
  }

  const clinicStoreInstance = new ClinicContextStore(clinicRefetcher, clinicId)

  if (token && clinicId) {
    clinicStoreInstance.changeClinic(Number(clinicId), token)
  }

  return clinicStoreInstance
}
